/**
 * Textos de las restricciones de los artículos (OBR-135): por qué un campo está bloqueado, qué se puede hacer en su lugar,
 * la corrección controlada de un administrador y el informe de la edición masiva y del importador.
 * Los bloqueos y los avisos los decide `articleEditPolicy` (@obrava/shared); aquí solo está el texto.
 */
export const articleRestrictionsEs = {
  'backoffice.articleRestrictions': {
    // ── Estado de uso (el aviso de la ficha) ───────────────────────────────────────────────────
    usageBannerE1:
      'Este artículo ya está en recetas, pedidos, plantillas u otros documentos: algunos campos ya no se pueden cambiar.',
    usageBannerE2:
      'Este artículo ya tiene historial (movimientos, lotes, pesadas, recepciones o expediciones, aunque ahora no tenga existencias): algunos campos ya no se pueden cambiar.',
    usageBannerE3:
      'Este artículo tiene existencias e historial: algunos campos ya no se pueden cambiar.',

    // ── Por qué un campo está bloqueado ────────────────────────────────────────────────────────
    reasonSkuReferenced:
      'El código no se puede cambiar: el artículo ya está en recetas, pedidos, plantillas u otros documentos, que seguirían diciendo el código antiguo.',
    reasonSkuHistory:
      'El código no se puede cambiar: el artículo ya tiene historial (movimientos, lotes, recepciones…), aunque ahora no tenga existencias.',
    reasonBaseUnitReferenced:
      'La unidad base no se puede cambiar: el artículo ya está en recetas, pedidos u otros documentos, que se quedarían en la unidad antigua.',
    reasonBaseUnitHistory:
      'La unidad base no se puede cambiar: las existencias y el historial ya están contados en esta unidad.',
    reasonRecipeUnitRecipeLines:
      'La unidad de receta no se puede cambiar ni quitar: hay recetas publicadas que la usan.',
    reasonRecipeUnitMovements:
      'La unidad de receta no se puede cambiar ni quitar: ya hay movimientos o pesadas registrados en esta unidad.',
    reasonRecipeFactorRecipeLines:
      'La equivalencia no se puede cambiar: hay recetas publicadas que usan esta unidad.',
    reasonRecipeFactorMovements:
      'La equivalencia no se puede cambiar: ya hay movimientos o pesadas registrados en esta unidad.',
    reasonTypeIncompatible:
      'No se puede cambiar la tipología: este cambio altera cómo se compra o se elabora el artículo, y ya se ha usado.',
    reasonTypePublishedRecipe:
      'No se puede pasar entre materia prima y producto auxiliar: el artículo es ingrediente de una receta publicada.',
    typeUnavailable:
      'Algunas tipologías no se pueden elegir: el artículo ya se ha usado y solo puede pasar a una compatible (semielaborado ↔ producto terminado, materia prima ↔ producto auxiliar).',

    // ── Avisos (no bloquean: piden confirmación al guardar) ────────────────────────────────────
    warnRecipeUnit:
      'Este artículo ya está en documentos o tiene historial. Cambiar o quitar la unidad de receta puede afectar a recetas en borrador.',
    warnArticleType:
      'Este artículo ya se ha usado. Pasar entre materia prima y producto auxiliar cambia cómo se declara en la ficha y en la etiqueta.',
    pendingBlockedIntro: 'Este guardado no se puede hacer:',
    warnNeedsConfirm: 'Tendrás que confirmarlo al guardar.',
    confirmWarningsTitle: 'Confirma el cambio',
    confirmWarningsIntro: 'Este guardado incluye cambios en un artículo que ya se ha usado:',
    confirmWarningsConfirm: 'Guardar igualmente',

    // ── Qué se puede hacer en su lugar ─────────────────────────────────────────────────────────
    alternativeNewArticle: 'Si hace falta, crea un artículo nuevo y da de baja el antiguo.',
    alternativeCorrection:
      'Un administrador puede hacer una corrección controlada, con motivo.',

    // ── Corrección controlada (administrador) ──────────────────────────────────────────────────
    correctionAction: 'Corrección controlada',
    correctionTitleSku: 'Corrección controlada del código',
    correctionTitleType: 'Corrección controlada de la tipología',
    correctionIntro:
      'Solo para corregir un error. Se guarda en el registro de actividad con el motivo y quién la ha hecho. Los documentos y el historial que ya existen no se modifican.',
    correctionSkuLabel: 'Código nuevo',
    correctionTypeLabel: 'Tipología nueva',
    correctionReasonLabel: 'Motivo (obligatorio)',
    correctionReasonHint: '¿Por qué hay que corregirlo?',
    correctionConfirm: 'Corregir',
    correctionDone: 'Corrección hecha y registrada.',
    correctionUnsavedHint:
      'Guarda primero los cambios de la ficha: la corrección se hace al instante y los recargaría.',
    correctionErrorForbidden: 'Solo un administrador puede hacer una corrección controlada.',
    correctionErrorReasonRequired: 'Escribe el motivo.',
    correctionErrorValueRequired: 'Escribe el valor nuevo.',
    correctionErrorValueInvalid: 'El valor no es válido.',
    correctionErrorInUse:
      'La tipología de este artículo no se puede corregir: es el resultado de una receta o tiene lotes de producto terminado u órdenes de producción. Crea un artículo nuevo y da de baja el antiguo.',
    correctionErrorSkuInvalid:
      'El código no es válido: como máximo 64 caracteres, de una sola línea, sin tabuladores ni espacios al principio o al final.',
    correctionTypeInUse:
      'La tipología de este artículo no se puede corregir: es el resultado de una receta o tiene lotes de producto terminado u órdenes de producción.',
    correctionErrorUnchanged: 'El valor nuevo es igual que el actual.',
    correctionErrorRecipeUnit:
      'Quita primero la unidad de receta: solo una materia prima puede tenerla.',
    correctionErrorNotFound: 'No se ha encontrado el artículo (quizá se ha dado de baja).',
    correctionErrorDuplicate: 'Ya hay otro artículo con este código.',
    correctionErrorGeneric: 'No se ha podido hacer la corrección.',

    // ── El servidor lo ha rechazado (alguien ha usado el artículo mientras editabas) ───────────
    serverLocked:
      'El servidor no ha dejado hacer el cambio de «{{field}}»: el artículo ya se ha usado. Recarga la ficha para ver su estado.',
    fieldSku: 'Código',
    fieldBaseUnit: 'Unidad base',
    fieldRecipeUnit: 'Unidad de receta',
    fieldRecipeUnitFactor: 'Equivalencia de la unidad de receta',
    fieldArticleType: 'Tipología',

    // ── Edición masiva ─────────────────────────────────────────────────────────────────────────
    bulkPreview_one:
      '{{count}} de los artículos seleccionados no se podrá cambiar porque ya se ha usado. Se detallará al terminar; el resto se actualizará.',
    bulkPreview_other:
      '{{count}} de los artículos seleccionados no se podrán cambiar porque ya se han usado. Se detallará al terminar; el resto se actualizará.',
    bulkPreviewLoading: 'Mirando qué artículos se pueden cambiar…',
    bulkReportTitle: 'Resultado de la edición masiva',
    bulkReportSummary_one: '{{done}} de {{total}} artículo actualizado; {{count}} no se ha podido cambiar.',
    bulkReportSummary_other:
      '{{done}} de {{total}} artículos actualizados; {{count}} no se han podido cambiar.',
    bulkReportBlockedHeading: 'No se han cambiado',
    bulkReportBlockedNote:
      'Estos artículos ya se han usado y el cambio no se permite. El resto se ha actualizado; ningún otro artículo se ha tocado.',
    bulkReportNothingDone: 'No se ha podido cambiar ningún artículo.',
    bulkReportClose: 'Cerrar',

    // ── Importador ─────────────────────────────────────────────────────────────────────────────
    importRowBlocked: 'Esta fila no se importará: {{reason}}',
    importRowWarning: 'Aviso: {{reason}}',
  },
} as const;
